import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Qualification } from '../../../models/qualification';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-qualification-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './qualification-list.component.html',
  styleUrl: './qualification-list.component.scss'
})
export class QualificationListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  qualifications = signal<Qualification[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadQualifications();
  }

  loadQualifications(): void {
    this.spinnerService.show();
    this.lutService.getQualifications().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortQualifications(data);
        this.qualifications.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading qualifications:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortQualifications(qualifications: Qualification[]): Qualification[] {
    return [...qualifications].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateQualification(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(qualification: Qualification): void {
  }

  onEditQualification(qualification: Qualification): void {
  }

  onDeleteQualification(qualification: Qualification): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(qualification.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalQualifications = this.qualifications();

          // 1. Optimistically remove from local state immediately
          this.qualifications.update(qualifications => qualifications.filter(q => q.id !== qualification.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteQualification(qualification.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Qualification deleted successfully');
              // Update cache so other components get updated data
              this.lookupDataService.updateTableData('qualifications', this.qualifications());
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting qualification:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.qualifications.set(originalQualifications);
              this.toast.error(`<strong>Failed to delete Qualification:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: Qualification, newName: string} | {item: Qualification, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: Qualification;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.qualifications.update(qualifications => {
      const updated = qualifications.map(q => q.id === event.item.id ? updatedItem : q);
      return this.sortQualifications(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateQualification(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Qualification updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('qualifications', this.qualifications());
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.qualifications.update(qualifications => {
          const updated = qualifications.map(q => q.id === event.item.id ? response : q);
          return this.sortQualifications(updated);
        });
      },
      error: (error) => {
        console.error('Error updating qualification:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.qualifications.update(qualifications =>
          qualifications.map(q => q.id === event.item.id ? originalItem : q)
        );
        this.toast.error(`<strong>Failed to update Qualification:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newQualification: Qualification = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.qualifications.update(qualifications => this.sortQualifications([...qualifications, newQualification]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createQualification({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Qualification created successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('qualifications', this.qualifications());
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.qualifications.update(qualifications => {
          const updated = qualifications.map(q => q.id === tempId ? response : q);
          return this.sortQualifications(updated);
        });
      },
      error: (error) => {
        console.error('Error creating qualification:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.qualifications.update(qualifications =>
          qualifications.filter(q => q.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Qualification:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
